import React, {
  createContext,
  useState,
  useEffect,
  useContext,
  useCallback,
  useRef,
} from "react";
import {
  getCurrentUser,
  logout as authLogout,
  setAuthToken,
  getUserProfile,
  isAuthError,
} from "../services/authService";
import {
  isAzureAuthEnabled,
  logoutFromAzureAD,
} from "../services/azureAuthService";
import {
  isMsalConfigured,
  trySilentSso,
  exchangeTokenForJwt,
  exchangeExternalIdTokenForJwt,
  logoutMicrosoftRedirect,
  getMicrosoftPhoto,
  getLoginHintFromUrl,
} from "../services/msalAuthService";

// Create the context
export const AuthContext = createContext(null);

// Custom hook to use the auth context
export const useAuth = () => {
  return useContext(AuthContext);
};

// Provider component
export const AuthProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(null);
  const [authMethod, setAuthMethod] = useState(null); // 'jwt', 'azure_ad', or 'msal'
  const [isLoading, setIsLoading] = useState(true);
  const [viewAsEstimator, setViewAsEstimator] = useState(false); // Admin view override
  const tokenRefreshTimerRef = useRef(null);
  const hasInitializedAuthRef = useRef(false);

  const getIdTokenFromUrl = () => {
    const queryParams = new URLSearchParams(window.location.search);
    let idToken = queryParams.get("id_token");
    if (idToken) return { idToken, source: "search" };

    const hash = window.location.hash || "";
    const hashParams = new URLSearchParams(hash.replace(/^#/, ""));
    idToken = hashParams.get("id_token");
    if (idToken) return { idToken, source: "hash" };

    return { idToken: null, source: null };
  };

  const clearIdTokenFromUrl = (source) => {
    if (source === "search") {
      const url = new URL(window.location.href);
      url.searchParams.delete("id_token");
      window.history.replaceState({}, document.title, url.toString());
      return;
    }
    if (source === "hash") {
      const hashParams = new URLSearchParams(
        window.location.hash.replace(/^#/, ""),
      );
      hashParams.delete("id_token");
      const newHash = hashParams.toString();
      const newUrl = `${window.location.pathname}${window.location.search}${newHash ? `#${newHash}` : ""}`;
      window.history.replaceState({}, document.title, newUrl);
    }
  };

  // Handle user logout - moved above useEffect and wrapped in useCallback
  const handleLogout = useCallback(() => {
    // Clear local JWT auth
    authLogout();
    setCurrentUser(null);
    const currentAuthMethod = authMethod;
    setAuthMethod(null);

    // Clear token refresh timer
    if (tokenRefreshTimerRef.current) {
      clearInterval(tokenRefreshTimerRef.current);
      tokenRefreshTimerRef.current = null;
    }

    // Logout based on auth method used
    if (currentAuthMethod === "msal") {
      logoutMicrosoftRedirect();
    } else if (isAzureAuthEnabled()) {
      logoutFromAzureAD("/");
    }
  }, [authMethod]);

  // Initialize auth state on component mount
  useEffect(() => {
    if (!hasInitializedAuthRef.current) {
      hasInitializedAuthRef.current = true;

      const initializeAuth = async () => {
        setIsLoading(true);

        const tryHydrateFromStoredJwt = async () => {
          const existingToken = localStorage.getItem("accessToken");
          if (!existingToken) return null;

          try {
            setAuthToken(existingToken);
            const freshUserData = await getUserProfile();
            if (!freshUserData) return null;

            localStorage.setItem("user", JSON.stringify(freshUserData));
            return freshUserData;
          } catch (error) {
            console.warn(
              "AuthContext init - Stored JWT hydration failed:",
              error,
            );
            if (isAuthError(error)) {
              authLogout();
            }
            return null;
          }
        };

        try {
          // ── Step 0: Platform SSO handoff via id_token in URL ──
          const { idToken, source } = getIdTokenFromUrl();
          if (idToken) {
            console.log(
              "AuthContext init - Found id_token in URL, exchanging...",
            );
            const jwtUser = await exchangeExternalIdTokenForJwt(idToken);
            clearIdTokenFromUrl(source);

            if (jwtUser) {
              setAuthToken(localStorage.getItem("accessToken"));
              setCurrentUser(jwtUser);
              setAuthMethod("msal");
              setIsLoading(false);
              return;
            }
            console.warn(
              "AuthContext init - id_token exchange failed, continuing...",
            );
          }

          // ── Step 1: MSAL silent SSO (cached account or login_hint from Platform) ──
          if (isMsalConfigured()) {
            const loginHint = getLoginHintFromUrl();
            console.log(
              "AuthContext init - Trying MSAL silent SSO",
              loginHint
                ? `(login_hint: ${loginHint})`
                : "(no login_hint, checking cached accounts)",
            );
            const msalUser = await trySilentSso();

            if (msalUser) {
              console.log(
                "AuthContext init - MSAL SSO successful:",
                msalUser.email,
              );

              const photo = await getMicrosoftPhoto();
              if (photo) {
                msalUser.photo = photo;
              }

              const jwtUser = await exchangeTokenForJwt();
              if (jwtUser) {
                console.log("AuthContext init - Token exchange successful");
                if (photo) jwtUser.photo = photo;
                setCurrentUser(jwtUser);
                setAuthMethod("msal");
                setIsLoading(false);
                return;
              }

              // Token exchange failed — try stored JWT as fallback
              console.warn(
                "AuthContext init - MSAL token exchange failed, trying stored JWT...",
              );
              const cachedJwtUser = await tryHydrateFromStoredJwt();
              if (cachedJwtUser) {
                if (photo && !cachedJwtUser.photo) cachedJwtUser.photo = photo;
                setCurrentUser(cachedJwtUser);
                setAuthMethod("jwt");
                setIsLoading(false);
                return;
              }

              console.warn("AuthContext init - No usable JWT after MSAL SSO");
              setCurrentUser(null);
              setAuthMethod(null);
              setIsLoading(false);
              return;
            }
            console.log("AuthContext init - No MSAL session found");
          }

          // ── Step 2: Stored JWT from a previous session ──
          const token = localStorage.getItem("accessToken");
          console.log("AuthContext init - JWT Token exists:", !!token);

          if (token) {
            setAuthToken(token);
            const userData = getCurrentUser();

            if (!userData) {
              // Token exists but no cached user — try fetching from API
              try {
                const freshUserData = await getUserProfile();
                if (freshUserData) {
                  setCurrentUser(freshUserData);
                  setAuthMethod("jwt");
                  localStorage.setItem("user", JSON.stringify(freshUserData));
                  return;
                }
              } catch (fetchError) {
                if (isAuthError(fetchError)) {
                  console.warn(
                    "AuthContext init - Stored JWT is invalid, clearing",
                  );
                  authLogout();
                }
                setCurrentUser(null);
                setAuthMethod(null);
                return;
              }
            } else {
              // Refresh profile in background, fall back to cached data on error
              try {
                const freshUserData = await getUserProfile();
                if (freshUserData) {
                  setCurrentUser(freshUserData);
                  setAuthMethod("jwt");
                  localStorage.setItem("user", JSON.stringify(freshUserData));
                  return;
                }
              } catch (profileError) {
                if (isAuthError(profileError)) {
                  console.warn(
                    "AuthContext init - Backend rejected stored JWT, clearing",
                  );
                  authLogout();
                  setCurrentUser(null);
                  setAuthMethod(null);
                  return;
                }
                console.warn(
                  "AuthContext init - Could not refresh profile, using cached data",
                );
              }

              setCurrentUser(userData);
              setAuthMethod("jwt");
              return;
            }
          }

          // ── Step 3: No auth — guest ──
          console.log(
            "AuthContext init - No authentication found, user is guest",
          );
        } catch (error) {
          console.error("Auth initialization error:", error);
          // Clear invalid auth state
          authLogout();
          setCurrentUser(null);
          setAuthMethod(null);
        } finally {
          setIsLoading(false);
        }
      };

      initializeAuth();
    }

    const handleAuthInvalidated = () => {
      setCurrentUser(null);
      setAuthMethod(null);
      setIsLoading(false);
      if (tokenRefreshTimerRef.current) {
        clearInterval(tokenRefreshTimerRef.current);
        tokenRefreshTimerRef.current = null;
      }
    };

    window.addEventListener("auth:logout", handleAuthInvalidated);

    // Clear timer on component unmount
    return () => {
      window.removeEventListener("auth:logout", handleAuthInvalidated);
      if (tokenRefreshTimerRef.current) {
        clearInterval(tokenRefreshTimerRef.current);
      }
    };
  }, []);

  // Update current user (useful after profile updates)
  const updateCurrentUser = useCallback((userData) => {
    setCurrentUser(userData);
    localStorage.setItem("user", JSON.stringify(userData));
  }, []);

  // Add updateUserData to update the user state
  const updateUserData = (newUserData) => {
    console.log("AuthContext: Updating user data with:", newUserData);
    console.log("AuthContext: Current user before update:", currentUser);

    // If newUserData is null or undefined, don't update
    if (!newUserData) {
      console.error(
        "AuthContext: Attempted to update with null/undefined user data",
      );
      return;
    }

    // Merge the new data with the existing user data
    const updatedUser = {
      ...currentUser, // Keep all existing user properties
      ...newUserData, // Override with any new properties
    };

    console.log("AuthContext: Merged user data:", updatedUser);

    // Update state and localStorage
    setCurrentUser(updatedUser);
    localStorage.setItem("user", JSON.stringify(updatedUser));
  };

  // Check if user is admin - handle multiple possible formats
  const checkisAdmin = (user) => {
    if (!user) return false;

    // Check different possible formats for role information
    return (
      user.role === "admin" ||
      user.role === "UserRole.admin" || // Added to handle enum string format
      user.role?.includes("admin") || // Added to handle any format containing ADMIN
      user.is_admin === true ||
      user.isAdmin === true ||
      (typeof user.role === "number" && user.role === 1)
    );
  };

  const hasApiAccessToken = !!localStorage.getItem("accessToken");
  const isAuthenticated = !!currentUser && hasApiAccessToken;

  // Computed admin status (respects viewAsEstimator override)
  const actualIsAdmin = checkisAdmin(currentUser);
  const effectiveIsAdmin = actualIsAdmin && !viewAsEstimator;

  // Toggle view mode (only works if user is actually an admin)
  const toggleViewMode = () => {
    if (actualIsAdmin) {
      setViewAsEstimator((prev) => !prev);
    }
  };

  // Value to be provided by the context
  // const value = {
  //   currentUser,
  //   isAuthenticated: !!currentUser,
  //   isAdmin: checkisAdmin(currentUser), // <-- Change from isAdmin to isAdmin
  //   logout: handleLogout,
  //   updateCurrentUser,
  //   updateUserData
  // };

  return (
    <AuthContext.Provider
      value={{
        currentUser,
        isAuthenticated,
        hasApiAccessToken,
        isAdmin: effectiveIsAdmin,
        actualIsAdmin, // True admin status (ignores view mode)
        viewAsEstimator, // Current view mode
        toggleViewMode, // Function to toggle view mode
        isLoading,
        authMethod, // 'jwt' or 'msal'
        logout: handleLogout,
        updateCurrentUser,
        updateUserData,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
